import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faAngleLeft, faAngleUp } from '@fortawesome/free-solid-svg-icons';
import { CommandPaletteContext } from '../../shell/command-palette/CommandPaletteProvider.jsx';
import { resolvePageMenu } from './navLookup.js';
import { useMenuNavTree } from './useMenuNavTree.js';
import { PageMenu } from './PageMenu.jsx';
import { PageHeaderTabs } from './PageHeaderTabs.jsx';
import './PageHeader.css';

export const FormHeader = ({
    onBack,
    parentTitle,
    parentPagePath,
    formTitle,
    actions,
    onNavigate,
    // See PageHeader: optional tab strip rendered in a second row below the
    // breadcrumb. With tabs, `actions` move down beside the tab strip.
    tabs,
    activeTab,
    onTabChange,
    // Optional override for the title-dropdown contents. When provided, PageMenu
    // lists these (shape: { parentLabel, grandparentLabel, sisters, parentSisters })
    // instead of the nav-tree-derived siblings — for pages whose child routes carry
    // params the static nav can't express (e.g. Game Workspace's per-game tabs).
    // `menuCurrentPath` is the path checked as current within that override.
    menuContext,
    menuCurrentPath,
}) => {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const palette = useContext(CommandPaletteContext);
    const openCommandPalette = palette?.open || (() => {});

    const [open, setOpen] = useState(false);
    const rootRef = useRef(null);

    const navTree = useMenuNavTree();
    const resolvedContext = useMemo(
        () => resolvePageMenu(parentPagePath, pathname, navTree),
        [parentPagePath, pathname, navTree]
    );
    // A caller-supplied menuContext wins over the nav-tree lookup (see prop docs).
    const context = menuContext || resolvedContext;

    useEffect(() => {
        if (!open) return;
        const onDown = (e) => {
            // Flyout sub-menus are portaled to document.body, so a DOM
            // contains() check on rootRef misses them. Treat any click
            // inside a flyout wrap as inside the menu.
            if (rootRef.current?.contains(e.target)) return;
            if (e.target.closest?.('.page-menu-flyout-wrap')) return;
            setOpen(false);
        };
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    const handlePick = (path) => {
        if (onNavigate) onNavigate(path);
        else navigate(path);
    };

    const hasTabs = tabs?.length > 0;

    return (
        <div className="content-header page-header form-header" ref={rootRef}>
            <div className="page-header-main">
                <div className="page-header-left">
                    {onBack && (
                        <button
                            type="button"
                            className="form-header-back"
                            onClick={onBack}
                            aria-label="Back"
                        >
                            <FontAwesomeIcon icon={faAngleLeft} />
                        </button>
                    )}
                    <button
                        type="button"
                        className={`page-header-trigger ${open ? 'open' : ''}`}
                        onClick={() => setOpen(v => !v)}
                        aria-haspopup="menu"
                        aria-expanded={open}
                    >
                        <h1 className="page-header-title">{parentTitle}</h1>
                        <FontAwesomeIcon
                            icon={open ? faAngleUp : faAngleDown}
                            className="page-header-caret"
                        />
                    </button>
                    {formTitle && (
                        <>
                            <span className="form-header-sep">/</span>
                            <span className="form-header-form-title">{formTitle}</span>
                        </>
                    )}
                    {open && (
                        <PageMenu
                            context={context}
                            currentPath={menuContext ? menuCurrentPath : resolvedContext?.currentNode?.path}
                            onPick={handlePick}
                            onSearch={openCommandPalette}
                            onClose={() => setOpen(false)}
                        />
                    )}
                </div>
                {actions && !hasTabs && <div className="page-header-actions">{actions}</div>}
            </div>
            {hasTabs && (
                <div className="page-header-subheader">
                    <PageHeaderTabs tabs={tabs} activeTab={activeTab} onTabChange={onTabChange} />
                    {actions && <div className="page-header-actions">{actions}</div>}
                </div>
            )}
        </div>
    );
};

export default FormHeader;
